import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_TextEffects() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔤 Advanced CSS: Text Effects</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS provides many creative ways to style text using{" "}
        <b>
          shadows, strokes, gradients, outlines, glow effects, and animations
        </b>
        . These effects can make headings and UI components stand out while
        keeping text accessible.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>text-shadow</code> allows multiple shadows for glow & 3D
            effects.
          </li>
          <li>
            <code>-webkit-text-stroke</code> creates text outlines (not in all
            browsers).
          </li>
          <li>
            <code>background-clip: text</code> + <code>color: transparent</code>{" "}
            enables gradient text.
          </li>
          <li>
            Neon effects use <code>text-shadow</code> with bright colors.
          </li>
          <li>
            You can animate text with <code>@keyframes</code> for glowing and
            typing effects.
          </li>
        </ul>
      </div>

      {/* Text Shadow */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Text Shadow</h2>
        <div
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#111",
            textShadow: "2px 2px 5px rgba(0,0,0,0.4)",
            marginBottom: "1rem",
          }}
        >
          Shadowed Text
        </div>
        <CodeCopyBlock
          code={`h1 { text-shadow: 2px 2px 5px rgba(0,0,0,0.4); }`}
        />
      </div>

      {/* Neon Glow */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 Neon Glow Effect</h2>
        <div
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#0ff",
            textShadow:
              "0 0 5px #0ff, 0 0 10px #0ff, 0 0 20px #0ff, 0 0 40px #0ff",
            marginBottom: "1rem",
          }}
        >
          Neon Glow
        </div>
        <CodeCopyBlock
          code={`h1 {
  color: #0ff;
  text-shadow:
    0 0 5px #0ff,
    0 0 10px #0ff,
    0 0 20px #0ff,
    0 0 40px #0ff;
}`}
        />
      </div>

      {/* Gradient Text */}
      <div className="tutorial-section">
        <h2 className="section-title">🌈 Gradient Text</h2>
        <div
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            background: "linear-gradient(90deg, #f43f5e, #3b82f6)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "1rem",
          }}
        >
          Gradient Text
        </div>
        <CodeCopyBlock
          code={`h1 {
  background: linear-gradient(90deg, #f43f5e, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}`}
        />
      </div>

      {/* Stroke Text */}
      <div className="tutorial-section">
        <h2 className="section-title">✏️ Stroke Text</h2>
        <div
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            color: "white",
            WebkitTextStroke: "2px black",
            marginBottom: "1rem",
          }}
        >
          Stroked Text
        </div>
        <CodeCopyBlock
          code={`h1 {
  color: white;
  -webkit-text-stroke: 2px black; /* Chrome & Safari */
}`}
        />
      </div>

      {/* 3D Text Effect */}
      <div className="tutorial-section">
        <h2 className="section-title">🕹️ 3D Text Effect</h2>
        <div
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#111",
            textShadow:
              "1px 1px 0 #fff, 2px 2px 0 #ccc, 3px 3px 0 #999, 4px 4px 0 #666",
            marginBottom: "1rem",
          }}
        >
          3D Text
        </div>
        <CodeCopyBlock
          code={`h1 {
  color: #111;
  text-shadow:
    1px 1px 0 #fff,
    2px 2px 0 #ccc,
    3px 3px 0 #999,
    4px 4px 0 #666;
}`}
        />
      </div>

      {/* Animated Glow */}
      <div className="tutorial-section">
        <h2 className="section-title">🎆 Animated Glow</h2>
        <div
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#f59e0b",
            animation: "glow 1.5s ease-in-out infinite alternate",
          }}
        >
          Glowing Text
        </div>
        <style>{`
          @keyframes glow {
            from {
              text-shadow: 0 0 5px #f59e0b, 0 0 10px #fbbf24;
            }
            to {
              text-shadow: 0 0 20px #f59e0b, 0 0 40px #fbbf24;
            }
          }
        `}</style>
        <CodeCopyBlock
          code={`@keyframes glow {
  from { text-shadow: 0 0 5px #f59e0b, 0 0 10px #fbbf24; }
  to { text-shadow: 0 0 20px #f59e0b, 0 0 40px #fbbf24; }
}

h1 {
  color: #f59e0b;
  animation: glow 1.5s ease-in-out infinite alternate;
}`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine <code>text-shadow</code> with <b>hover effects</b> for
            glowing buttons.
          </li>
          <li>
            Use <code>clip-path</code> & <code>mask-image</code> for futuristic
            text effects.
          </li>
          <li>
            Experiment with <code>SVG filters</code> for advanced text styling.
          </li>
          <li>
            Apply <b>animated gradients</b> for dynamic headings.
          </li>
          <li>
            Try <code>CSS Variables</code> to manage colors for text themes.
          </li>
        </ul>
      </div>
    </div>
  );
}
